import React, { useState } from "react";
import { Col, Card } from "antd";
import { Chart, Geom, Axis, Tooltip, Legend } from "bizcharts";
import EmptyData from "./emptyData";
import ChartHelp from "./chartHelp";
import DataSet from "@antv/data-set";

interface Props {
    metadata: any;
    dataSource: {
        name: string;
        percentage: number;
        data: {
            [key: string]: number;
        };
    }[];
}

const COLORS = [
    "#3182bd",
    "#54ca76",
    "#363c27",
    "#82b66c",
    "#4aaa82",
    "#da9760",
    "#7503f7",
    "#04431d",
    "#f78dd5",
    "#4d154a",
];

const BarChartWithPercentage = ({ metadata, dataSource }: Props) => {
    const [chartIns, setChartIns] = useState<G2.Chart | undefined>(undefined);

    let data: any[] = [],
        fields: string[] = [];

    if (dataSource && dataSource.length !== 0) {
        data = dataSource.map((item) => {
            let data: any = {};
            Object.keys(item.data).forEach((key) => {
                data[key] = Number(item.data[key]);
                if (!fields.includes(key)) {
                    fields.push(key);
                }
            });

            return {
                label: item.name,
                Percentagem: Number(item.percentage),
                ...data,
            };
        });
    }

    if (metadata.checkData && !dataSource) {
        return null;
    }

    const ds = new DataSet();
    const dv = ds.createView().source(data);
    dv.transform({
        type: "fold",
        fields,
        key: "type",
        value: "value",
    });

    const scale = {
        Percentagem: {
            type: "linear",
            min: 0,
            max: 100,
            alias: "Percentagem",
            formatter: (val: any) => {
                return val + "%";
            },
        },
        value: {
            alias: "Quantidade",
        },
    };

    const getG2Instance = (chart: G2.Chart) => {
        setChartIns(chart);
    };

    return (
        <Col xs={24} md={metadata?.colSize || 12}>
            <Card
                size="small"
                title={metadata.title ? metadata.title : " "}
                extra={metadata.help ? <ChartHelp text={metadata.help} /> : null}
                className={"qa-title-dashboard"}
            >
                {!dataSource || dataSource.length === 0 ? (
                    <EmptyData />
                ) : (
                    <Chart
                        height={400}
                        data={dv}
                        scale={scale}
                        padding={"auto"}
                        forceFit
                        onGetG2Instance={getG2Instance}
                    >
                        <Legend
                            custom
                            allowAllCanceled
                            items={[
                                ...fields.map((field, index) => {
                                    const color =
                                        field === "Planeado"
                                            ? "#3182bd"
                                            : field === "Executado"
                                            ? "#54ca76"
                                            : COLORS[index];

                                    return { value: field, marker: { symbol: "square", fill: color, radius: 5 } };
                                }),
                                {
                                    value: "Percentagem",
                                    marker: { symbol: "hyphen", stroke: "#da9760", radius: 5, lineWidth: 3 },
                                },
                            ]}
                            onClick={(ev: any) => {
                                const item = ev.item;
                                const value = item.value;
                                const checked = ev.checked;

                                if (!chartIns) return;

                                const geoms = chartIns.getAllGeoms();
                                for (let i = 0; i < geoms.length; i++) {
                                    const geom: any = geoms[i];
                                    if (geom.getYScale().field === value && value === "Percentagem") {
                                        if (checked) {
                                            geom.show();
                                        } else {
                                            geom.hide();
                                        }
                                    } else if (geom.getYScale().field === "value" && value !== "Percentagem") {
                                        geom.getShapes().map((shape: any) => {
                                            if (shape._cfg.origin._origin.type == value) {
                                                shape._cfg.visible = !shape._cfg.visible;
                                            }
                                            shape.get("canvas").draw();
                                            return shape;
                                        });
                                    }
                                }
                            }}
                        />
                        <Axis name="label" />
                        <Axis name="value" position={"left"} title />
                        <Axis
                            name="Percentagem"
                            grid={null}
                            title
                            label={{
                                textStyle: {
                                    fill: "#fdae6b",
                                },
                            }}
                        />
                        <Tooltip />
                        <Geom
                            type="interval"
                            position="label*value"
                            color={[
                                "type",
                                (value: string) => {
                                    const color =
                                        value === "Planeado"
                                            ? "#3182bd"
                                            : value === "Executado"
                                            ? "#54ca76"
                                            : COLORS[fields.indexOf(value)];

                                    return color || "";
                                },
                            ]}
                            adjust={[
                                {
                                    type: "dodge",
                                    marginRatio: 1 / 32,
                                },
                            ]}
                        />
                        <Geom
                            type="line"
                            position="label*Percentagem"
                            color="#fdae6b"
                            size={3}
                            shape="smooth"
                            tooltip={[
                                "Percentagem",
                                (Percentagem) => {
                                    return {
                                        name: "Percentagem de Execução",
                                        value: Percentagem + "%",
                                    };
                                },
                            ]}
                        />
                        <Geom type="point" position="label*Percentagem" color="#fdae6b" size={3} shape="circle" />
                    </Chart>
                )}
            </Card>
        </Col>
    );
};

export default BarChartWithPercentage;
